import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { ChatBoxRecipients } from './ChatBoxRecipients';
import { ChatBoxSuggestionsList } from './ChatBoxSuggestionsList';
import { ChatBoxMessages } from './ChatBoxMessages';
import { ChatBoxInput } from './ChatBoxInput';
import type { ContactSuggestion } from '../../types';
import { useChat, preselectedRecipientsAtom } from '../../hooks';
import { useContactSuggestions } from '../../hooks/use-contact-suggestions';
import { useCreateConversation } from '../../hooks/use-create-conversation';
import { useAtom } from 'jotai';

/**
 * The maximum number of recipients that can be added to a conversation. Read
 * the `createConversation` mutation for more details.
 */
const MAX_RECIPIENTS = 20;

/**
 * The suggestions list, messages, and input for the new message chat box.
 * @see {@link ChatBox}
 */
export function ChatBoxNewMessage({
    className,
    isOpen,
}: {
    className?: string;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
}) {
    const [search, setSearch] = useState('');
    const [recipients, setRecipients] = useState<ContactSuggestion[]>([]);
    const [preselectedRecipients, setPreselectedRecipients] = useAtom(
        preselectedRecipientsAtom
    );
    const searchInputRef = useRef<HTMLInputElement>();
    const { setPendingMessage, convertNewMessageToConversation } = useChat();
    const {
        suggestions,
        hasMore: hasMoreSuggestions,
        isLoading: isLoadingSuggestions,
        setSearch: setSearchSuggestions,
        loadMore,
    } = useContactSuggestions({ limit: 50 });
    const {
        conversation: conversationPreview,
        createdConversation,
        optimisticMessage,
        isLoading: isConversationLoading,
        isSubmitting: isCreatingConversation,
        createConversation,
    } = useCreateConversation({
        participantIDs: recipients.map((r) => r.ID),
    });
    const messages = useMemo(() => {
        const previewMessages = conversationPreview?.Messages.nodes ?? [];
        if (optimisticMessage) {
            return [optimisticMessage, ...previewMessages];
        }
        return previewMessages;
    }, [conversationPreview, optimisticMessage]);

    const handleInputRef = useCallback((element?: HTMLInputElement) => {
        searchInputRef.current = element;
    }, []);

    const handleSearchChange = useCallback(
        (search: string, excludeMemberIDs?: number[]) => {
            setSearch(search);
            setSearchSuggestions(search, excludeMemberIDs);
        },
        [setSearchSuggestions]
    );

    function handleRecipientsChange(recipients: ContactSuggestion[]) {
        setRecipients(recipients);
        handleSearchChange(
            search,
            recipients.map((r) => r.ID)
        );
    }

    function addRecipient(suggestion: ContactSuggestion) {
        setRecipients((prev) => {
            if (
                prev.some((r) => r.ID === suggestion.ID) ||
                prev.length >= MAX_RECIPIENTS
            ) {
                return prev;
            }
            const newRecipients = [...prev, suggestion];
            handleSearchChange(
                '',
                newRecipients.map((r) => r.ID)
            );
            return newRecipients;
        });
        searchInputRef.current?.focus();
    }

    // Load preselected recipients when the chat box opens
    useEffect(() => {
        if (
            isOpen &&
            preselectedRecipients.length > 0 &&
            suggestions.length > 0
        ) {
            // Find the contact suggestions matching the preselected recipient IDs
            const preselected = suggestions.filter((suggestion) =>
                preselectedRecipients.includes(suggestion.ID)
            );

            if (preselected.length > 0) {
                setRecipients(preselected);
                handleSearchChange(
                    '',
                    preselected.map((r) => r.ID)
                );
                // Clear the preselected recipients after using them
                setPreselectedRecipients([]);
            }
        }
    }, [
        isOpen,
        preselectedRecipients,
        suggestions,
        setPreselectedRecipients,
        handleSearchChange,
    ]);

    // Convert this new message chat box to a normal conversation chat box when
    // a new conversation is created.
    useEffect(() => {
        if (createdConversation) {
            convertNewMessageToConversation(Number(createdConversation.ID));
        }
    }, [createdConversation, convertNewMessageToConversation]);

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <ChatBoxRecipients
                search={search}
                recipients={recipients}
                maxRecipients={MAX_RECIPIENTS}
                isOpen={isOpen}
                onSearchChange={handleSearchChange}
                onRecipientsChange={handleRecipientsChange}
                onInputRef={handleInputRef}
            />
            <div className="h-px bg-white"></div>
            {recipients.length === 0 ||
            (search && recipients.length < MAX_RECIPIENTS) ? (
                <ChatBoxSuggestionsList
                    className="mt-[8px] min-h-0 flex-1"
                    isLoading={isLoadingSuggestions}
                    suggestions={suggestions}
                    hasMore={hasMoreSuggestions}
                    onSuggestionClick={addRecipient}
                    onLoadMore={loadMore}
                />
            ) : (
                <>
                    <ChatBoxMessages
                        className="min-h-0 flex-1"
                        conversation={conversationPreview ?? undefined}
                        messages={messages}
                        isOpen={isOpen}
                    />
                    <ChatBoxInput
                        isOpen={isOpen}
                        isDisabled={isConversationLoading}
                        isSubmitting={
                            isCreatingConversation || isConversationLoading
                        }
                        autoFocus={false}
                        onSend={({ message, attachments }) => {
                            if (conversationPreview) {
                                const conversationID = Number(
                                    conversationPreview.ID
                                );
                                setPendingMessage({
                                    conversationID,
                                    message,
                                    attachments,
                                });
                                convertNewMessageToConversation(conversationID);
                            } else {
                                // This new message chat box will be converted to
                                // a normal conversation chat box when this action
                                // is completed (see useEffect above).
                                createConversation(message, attachments);
                            }
                        }}
                    />
                </>
            )}
        </div>
    );
}
